/* eslint-disable no-param-reassign */
import { Service } from "./Service"
import DialogService from "./dialog"
import StringUtils from "../utils/stringUtils"
import { Color, GradientColor } from "../models/colors"
import { PageConfig } from "../models/pageConfig"
import { DialogType } from "../models/dialog"

class UIService extends Service {
    private static instance: UIService

    // palette disponible pour les editeurs de texte de l'app
    private colorMapppingTable: Array<{
        label: string
        style: string
        hex: string
        rgb: string
    }> = [
        {
            label: "checkInPurple",
            style: UIService.getColorByName(Color.checkInPurple),
            hex: "#200943",
            rgb: "rgb(9, 12, 59)",
        },
        {
            label: "redMoov",
            style: UIService.getColorByName(Color.redMoov),
            hex: "#C6222B",
            rgb: "rgb(198, 34, 43)",
        },
        {
            label: "red",
            style: UIService.getColorByName(Color.error),
            hex: "#f44336",
            rgb: "rgb(244, 67, 54)",
        },
        {
            label: "pink",
            style: UIService.getColorByName(Color.pink),
            hex: "#ff4081",
            rgb: "rgb(255, 255, 255)",
        },
        {
            label: "purple",
            style: UIService.getColorByName(Color.purple),
            hex: "#673ab7",
            rgb: "rgb(103, 58, 183)",
        },
        {
            label: "warning",
            style: UIService.getColorByName(Color.warning),
            hex: "#ffca00",
            rgb: "rgb(255, 202, 0)",
        },
        {
            label: "green",
            style: UIService.getColorByName(Color.green),
            hex: "#44b700",
            rgb: "rgb(68, 183, 0)",
        },
        {
            label: "mc4",
            style: UIService.getColorByName(Color.mc4),
            hex: "#00582f",
            rgb: "rgb(0, 88, 47)",
        },

        {
            label: "blue",
            style: UIService.getColorByName(Color.blue),
            hex: "#1976d2",
            rgb: "rgb(25, 118, 210)",
        },

        {
            label: "seaBlue",
            style: UIService.getColorByName(Color.seaBlue),
            hex: "#4F9DE2",
            rgb: "rgb(255, 255, 255)",
        },
        {
            label: "azur",
            style: UIService.getColorByName(Color.azur),
            hex: "#7F7FD5",
            rgb: "rgb(255, 255, 255)",
        },
        {
            label: "lightGrey",
            style: UIService.getColorByName(Color.lightGrey),
            hex: "#eeeeee",
            rgb: "rgb(238, 238, 238)",
        },

        {
            label: "greenGrey",
            style: UIService.getColorByName(Color.greenGrey),
            hex: "#335c6d",
            rgb: "rgb(51, 92, 109)",
        },

        {
            label: "blueGrey",
            style: UIService.getColorByName(Color.blueGrey),
            hex: "#313b46",
            rgb: "rgb(49, 59, 70)",
        },
        {
            label: "darkGrey",
            style: UIService.getColorByName(Color.darkGrey),
            hex: "#424242",
            rgb: "rgb(66, 66, 66)",
        },
        {
            label: "black",
            style: UIService.getColorByName(Color.black),
            hex: "#212121",
            rgb: "rgb(33, 33, 33)",
        },

        {
            label: "white",
            style: UIService.getColorByName(Color.white),
            hex: "#fff",
            rgb: "rgb(255, 255, 255)",
        },
        {
            label: "darkGreen",
            style: UIService.getColorByName(Color.darkGreen),
            hex: "#006400",
            rgb: "rgb(1, 50, 32)",
        },
    ]

    private palette: {
        palette: Array<{
            label: string
            style: string
            hex: string
            rgb: string
        }>
        colorStyleMap: any
        colorHtmlMappingExport: any
    } = {
        palette: [],
        colorStyleMap: null,
        colorHtmlMappingExport: null,
    }

    constructor() {
        super()
        // on set les attr de la palette
        this.palette = {
            palette: this.colorMapppingTable,
            colorStyleMap: this.colorMapppingTable.reduce(
                (acc, currentValue, currentIndex) => {
                    let res: any = {}
                    if (currentIndex === 1)
                        res[acc.label] = { color: acc.style }
                    else res = acc
                    res[currentValue.label] = { color: currentValue.style }
                    return res
                }
            ),
            colorHtmlMappingExport: this.colorMapppingTable.reduce(
                (acc, currentValue, currentIndex) => {
                    let res: any = {
                        inlineStyles: {},
                    }
                    if (currentIndex === 1)
                        res.inlineStyles[acc.label] = {
                            style: { color: acc.style },
                        }
                    else res = acc
                    res.inlineStyles[currentValue.label] = {
                        style: { color: currentValue.style },
                    }
                    return res
                }
            ),
        }
        // set le nom du service
        this.name = "UIService"
        // ajoute toute les methodes du service au tableau de mapping
        this.serviceMap = [
            { key: "showConfirmationToast", fct: this.showConfirmationToast },
            { key: "showErrorToast", fct: this.showErrorToast },
            { key: "showInfoToast", fct: this.showInfoToast },
            { key: "showDialog", fct: this.showDialog },
            { key: "hideDialog", fct: this.hideDialog },
            { key: "hidePopover", fct: this.hidePopover },
            { key: "showPopover", fct: this.showPopover },
            { key: "showModal", fct: this.showModal },
            { key: "hideModal", fct: this.hideModal },
        ]
    }

    /**
     * Récuperation de l'instance
     */
    public static getInstance(): UIService {
        if (!this.instance) {
            this.instance = new UIService()
        }
        return this.instance
    }

    /**
     * showToast renvoie le composant SnackBar avec les configurations nécessaires
     * @param type type de snackbar "error","success","warning","info"
     * @param text le message à afficher dans le toast
     * @param duration la durée d'affichage
     */
    public showToast = (
        type: string,
        text: string,
        sender: string,
        duration?: number,
        anchorOrigin?: {
            horizontal: "center" | "left" | "right"
            vertical: "bottom" | "top"
        }
    ): void => {
        DialogService.getInstance().send({
            type: DialogType.SHOW,
            to: "SnackBar",
            params: [
                {
                    open: true,
                    text,
                    type,
                    duration,
                    anchorOrigin,
                },
            ],
            sender,
        })
    }

    /**
     * showConfirmationToast renvoie le composant SnackBar  de success avec les configurations nécessaires
     * @param text le message à afficher dans le toast
     * @param sender le composant qui a envoyé l'abonnement
     */
    public showConfirmationToast = (
        text: string,
        sender: string
    ): Promise<any> => {
        const type: "success" | "info" | "warning" | "error" = "success"
        return DialogService.getInstance().send({
            type: DialogType.SHOW,
            to: "SnackBar",
            params: [
                {
                    open: true,
                    text,
                    type,
                    duration: 5000,
                    anchorOrigin: {
                        horizontal: "center",
                        vertical: "top",
                    },
                },
            ],
            sender,
        })
    }

    /**
     * showErrorToast renvoie le composant SnackBar  de error avec les configurations nécessaires
     * @param text le message à afficher dans le toast
     * @param sender le composant qui a envoyé l'abonnement
     */
    public showErrorToast = (text: string, sender: string): Promise<any> => {
        const type: "success" | "info" | "warning" | "error" = "error"
        const promise = new Promise((resolve) => {
            resolve(
                DialogService.getInstance().send({
                    type: DialogType.SHOW,
                    to: "SnackBar",
                    params: [
                        {
                            open: true,
                            text,
                            type,
                            duration: 5000,
                            anchorOrigin: {
                                horizontal: "center",
                                vertical: "top",
                            },
                        },
                    ],
                    sender,
                })
            )
        })
        return promise
    }

    /**
     * showInfoToast renvoie le composant SnackBar  de error avec les configurations nécessaires
     * @param text le message à afficher dans le toast
     * @param sender le composant qui a envoyé l'abonnement
     */
    public showInfoToast = (text: string, sender: string): Promise<any> => {
        const type: "success" | "info" | "warning" | "error" = "info"
        const promise = new Promise((resolve) => {
            resolve(
                DialogService.getInstance().send({
                    type: DialogType.SHOW,
                    to: "SnackBar",
                    params: [
                        {
                            open: true,
                            text,
                            type,
                            duration: 5000,
                            anchorOrigin: {
                                horizontal: "center",
                                vertical: "top",
                            },
                        },
                    ],
                    sender,
                })
            )
        })
        return promise
    }

    /**
     * hideDialog cache le Dialog
     */
    public hideDialog = (sender: string): Promise<any> => {
        return DialogService.getInstance().send({
            type: DialogType.SHOW,
            to: "CwDialog",
            params: [
                {
                    open: false,
                },
            ],
            sender,
        })
    }

    /**
     * showDialog affiche le Dialog avec les paramètres souhaité
     * @param params paramètre d'affichage du Dialog
     * @param sender le composant qui a envoyé l'abonnement
     */
    public showDialog = (
        params: {
            isOpen: boolean
            transition: "left" | "right" | "up" | "down" | undefined
            title: string
            content: PageConfig<any>[]
            actionContent: PageConfig<any>[]
            showTitle?: boolean
            contentClassName?: string
            actionsClassName?: string
            dialogClassName?: string
            noClickAway?: boolean
        },
        sender: string
    ): Promise<any> => {
        return DialogService.getInstance().send({
            type: DialogType.SHOW,
            to: "CwDialog",
            params: [params],
            sender,
        })
    }

    /**
     * showModal affiche la modal avec les paramètres souhaité
     * @param params paramètre d'affichage du modal
     * @param sender le composant qui a envoyé l'abonnement
     * @description MOBILE ONLY
     */
    public showModal = (
        params: {
            isOpen: boolean
            content: PageConfig<any>[]
            containerClass?: string
        },
        sender: string
    ): Promise<any> => {
        return DialogService.getInstance().send({
            type: DialogType.SHOW,
            to: "CmModal",
            params: [params],
            sender,
        })
    }

    /**
     * hideModal cache la modal
     * @param sender le composant qui a envoyé l'abonnement
     * @description MOBILE ONLY
     */
    public hideModal = (sender: string): Promise<any> => {
        return DialogService.getInstance().send({
            type: DialogType.SHOW,
            to: "CmModal",
            params: [
                {
                    isOpen: false,
                },
            ],
            sender,
        })
    }

    /**
     * hideToast changer le state de snackBar à false
     */
    public hideToast = (sender: string): void => {
        DialogService.getInstance().send({
            type: DialogType.SHOW,
            params: [
                {
                    open: false,
                },
            ],
            sender,
        })
    }

    /**
     * showPopover affiche le Dialog avec les paramètres souhaité
     * @param params paramètre d'affichage du Dialog
     * @param sender le composant qui a envoyé l'abonnement
     */
    public showPopover = (
        params: {
            anchorEl: HTMLElement | null
            anchorOrigin?:
                | {
                      vertical: "center" | "top" | "bottom" | number
                      horizontal: "left" | "right" | "center" | number
                  }
                | undefined
            transformOrigin?:
                | {
                      vertical: "center" | "top" | "bottom" | number
                      horizontal: "left" | "right" | "center" | number
                  }
                | undefined
            title?: string
            content: string | React.JSX.Element
        },
        sender: string
    ): Promise<any> => {
        return DialogService.getInstance().send({
            type: DialogType.SHOW,
            to: "CwPopover",
            params: [params],
            sender,
        })
    }

    /**
     * hidePopover changer le state de popover à null
     */
    public hidePopover = (sender: string): Promise<any> => {
        return DialogService.getInstance().send({
            type: DialogType.SHOW,
            params: [
                {
                    anchorEl: null,
                },
            ],
            sender,
        })
    }

    /**
     * getColorByName prend le nom de la couleur à partir de l'enum Color et renvoie les code Hexa de la couleur
     * @param name Nom de la couleur
     */
    public static getColorByName(name: Color | undefined | string): string {
        switch (name) {
            case "black":
                return "#212121"
            case "white":
                return "#fff"
            case "grey":
                return "#7d7d7d"
            case "darkGrey":
                return "#424242"
            case "lightGrey":
                return "#eeeeee"
            case "blueGrey":
                return "#313b46"
            case "mc4":
                return "#00582f"
            case "greenGrey":
                return "#335c6d"
            case "transparent":
                return "transparent"
            case "error":
                return "#f44336"
            case "success":
                return "#4caf50"
            case "warning":
                return "#ffca00"
            case "info":
                return "#2196f3"
            case "purple":
                return "#673ab7"
            case "blue":
                return "#1976d2"
            case "checkInGreen":
                return "#03DAC5"
            case "checkInPurple":
                return "#200943"
            case "darkBlue":
                return "#1a237e"
            case "lightBlue":
                return "#29b6f6"
            case "cyan":
                return "#f44336"
            case "green":
                return "#44b700"
            case "red":
                return "#f44336"
            case "redMoov":
                return "#C6222B"
            case "pink":
                return "#f292f4"
            case "azur":
                return "#7F7FD5"
            case "seaBlue":
                return "#4F9DE2"
            case "planningYellow":
                return "#efbf00"
            case "planningBlue":
                return "#0093ef"
            case "planningGreen":
                return "#00ef78"
            case "planningOrange":
                return "#ef7800"
            case "planningRed":
                return "#ef4800"
            case "darkGreen":
                return "#006400"
            case "darkViolet":
                return "#200046"
            case "payBlue":
                return "#35b5de"
            case "payBlueSecondary":
                return "#40c3e3"
            case "paySuccess":
                return "#67E9A6"
            case "payError":
                return "#EE4C4C"
            case "darkRed":
                return "#88181b"
            case "cityYello":
                return "#E7a404"
            case "cityYellow":
                return "#E8A405"
            case "cityGrey":
                return "#585858"
            case "statsYellow":
                return "#FFA305"
            case "statsBlue":
                return "#200943"
            default:
                // default black
                return "#212121"
        }
    }

    /**
     * Enumeration des gradients disponibles dans l'app et lors noms
     */
    public static gradients: Array<{ name: string; value: string }> = [
        { name: "miaka", value: "linear-gradient(to right, #16222a, #3a6073)" },
        {
            name: "dracula",
            value: "linear-gradient(to right, #dc2424, #4a569d)",
        },
        {
            name: "softPurple",
            value: "linear-gradient(to right, #1d2b64, #f8cdda)",
        },
        {
            name: "mantle",
            value: "linear-gradient(to right, #24c6dc, #514a9d)",
        },
        {
            name: "titanium",
            value: "linear-gradient(to right, #1a222f, #303d50)",
        },
        {
            name: "seaBlizz",
            value: "linear-gradient(to right, #1cd8d2, #93edc7)",
        },
        {
            name: "midnightCity",
            value: "linear-gradient(to right, #232526, #414345)",
        },
        {
            name: "shroomHaze",
            value: "linear-gradient(to right, #5c258d, #4389a2)",
        },
        {
            name: "veniceBlue",
            value: "linear-gradient(to right, #085078, #85d8ce)",
        },
        {
            name: "kashmir",
            value: "linear-gradient(to right, #614385, #516395)",
        },
        {
            name: "talkingToMiceElf",
            value: "linear-gradient(to right, #948e99, #2e1437)",
        },
        {
            name: "blueGrad",
            value: "linear-gradient(to right, #1FA2FF 0%, #12D8FA  51%, #1FA2FF  100%)",
        },
        {
            name: "yellowGrad",
            value: "linear-gradient(to right, #7474BF 0%, #348AC7  51%, #7474BF  100%)",
        },
        {
            name: "blackGrad",
            value: "linear-gradient(to right, #000000 0% , #212121 0%,#000000 40%)",
        },
        {
            name: "ohHappiness",
            value: "linear-gradient(to right, rgb(0, 176, 155), rgb(150, 201, 61))",
        },
        {
            name: "orangeCoral",
            value: "linear-gradient(to right, rgb(255, 153, 102), rgb(255, 94, 98))",
        },
        {
            name: "scooter",
            value: "linear-gradient(to left, rgb(54, 209, 220), rgb(91, 134, 229))",
        },
        {
            name: "azurLane",
            value: "linear-gradient(to right, rgb(127, 127, 213), rgb(134, 168, 231), rgb(145, 234, 228))",
        },
        {
            name: "mielGrad",
            value: "linear-gradient(60deg, #543ab7 0%, #00acc1 100%)",
        },
        {
            name: "citrusPeel",
            value: "linear-gradient(to right, rgb(253, 200, 48), rgb(243, 115, 53))",
        },
        {
            name: "cileaPay",
            value: "linear-gradient(135deg, hsla(267, 100%, 14%, 1) 8%, hsla(198, 78%, 55%, 1) 97%)",
        },
        {
            name: "orangeCity",
            value: "linear-gradient(90deg, rgb(255 140 0) 0%, rgb(255 211 92) 100%)",
        },
        {
            name: "cileamoov",
            value: "linear-gradient(269deg, #ff1300, #200943)",
        },
        {
            name: "cileamoov2",
            value: "linear-gradient(90deg, hsla(267, 100%, 14%, 1) 0%, hsla(208, 72%, 60%, 1) 100%)",
        },
        {
            name: "lightGreyGrad",
            value: "linear-gradient(90deg, hsla(200, 18%, 97%, 1) 0%, hsla(200, 18%, 97%, 1) 100%)",
        },
        {
            name: "velociti",
            value: "linear-gradient(90deg, hsla(42, 96%, 46%, 1) 0%, hsla(42, 96%, 46%, 1) 100%)",
        },
        {
            name: "checkInPurple",
            value: "linear-gradient(90deg, rgba(9,12,59,1) 0%, rgba(9,12,59,1) 100%)",
        },
        {
            name: "demat",
            value: "linear-gradient(133deg, rgba(145,31,32,1) 0%, rgba(145,5,14,1) 39%, rgba(255,85,103,1) 100%)",
        },
    ]

    /**
     * Retourne une régle CSS au hasard pour un gradient de fond
     * @returns Css string
     */
    public static getRandomLinearGrad(): string {
        return this.gradients[StringUtils.getRandomInt(0, 9)].value
    }

    /**
     * Retourne une régle CSS pour un gradient de fond en fonction de son nom
     * @param name nom du gradient
     * @param colorFallBack si true on va chercher dans les color unies
     * @returns Css string
     */
    public static getLinearGradByName(
        name: GradientColor | Color | undefined,
        colorFallBack = false
    ): string {
        const res = this.gradients.find((item) => item.name === name)
        if (res && name) {
            return res.value
        }
        if (colorFallBack && name) {
            return UIService.getColorByName(name as Color | undefined)
        }
        return "transparent"
    }

    public getColorPalette(): {
        palette: Array<{
            label: string
            style: string
            hex: string
            rgb: string
        }>
        colorStyleMap: any
        colorHtmlMappingExport: any
    } {
        return this.palette
    }

    public static getColors(colors?: string[]) {
        if (Array.isArray(colors) && colors.length) {
            return colors
        }
        return [
            "#e895f1",
            "#57afea",
            "#73f0cb",
            "#f8ca00",
            "#4fc3f7",
            "#ff8a65",
            "#4dd0e1",
            "#9575cd",
            "#81c784",
            "#fff176",
        ]
    }

    public static HexToRgba(hex: string, opacity = 1) {
        let hexa = hex.replace("#", "")

        if (hexa.length === 3) {
            hexa = hexa[0] + hexa[0] + hexa[1] + hexa[1] + hexa[2] + hexa[2]
        }

        const r = parseInt(hexa.substring(0, 2), 16)
        const g = parseInt(hexa.substring(2, 4), 16)
        const b = parseInt(hexa.substring(4, 6), 16)

        if (opacity > 1 && opacity <= 100) {
            opacity /= 100
        }

        return `rgba(${r},${g},${b},${opacity})`
    }

    /**
     * getTimeLineBackground retourne une couleur différente pour chaque groupe
     * @param index index du groupe
     */
    public getTimeLineBackground = (index: number): string => {
        switch (index) {
            case 0:
            case 6:
            case 12:
            case 18:
                return "#e91e63"
            case 1:
            case 7:
            case 13:
            case 19:
                return "#4caf50"
            case 2:
            case 8:
            case 14:
            case 20:
                return "#ff5722"
            case 3:
            case 9:
            case 15:
            case 21:
                return "#2196f3"
            case 4:
            case 10:
            case 16:
            case 22:
                return "#673ab7"

            case 5:
            case 11:
            case 17:
            case 23:
                return "#7F7FD5"

            default:
                // default blue
                return "#1976d2"
        }
    }
}

export default UIService
